import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import 'maplibre-gl/dist/maplibre-gl.css';
import '@maplibre/maplibre-gl-leaflet';

interface MapLibreTileLayerProps {
  style: string;
  attribution?: string;
  onError?: () => void;
}

export function MapLibreTileLayer({
  style,
  attribution,
  onError,
}: MapLibreTileLayerProps) {
  const map = useMap();
  const layerRef = useRef<L.MaplibreGL | null>(null);
  const onErrorRef = useRef(onError);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  useEffect(() => {
    const layer = L.maplibreGL({ style });
    layer.addTo(map);
    layerRef.current = layer;

    // Add custom attribution if provided
    if (attribution && map.attributionControl) {
      map.attributionControl.addAttribution(attribution);
    }

    const mlMap = layer.getMaplibreMap();

    // Apply Korean labels whenever style data loads
    mlMap.on('styledata', () => {
      for (const styleLayer of mlMap.getStyle().layers) {
        if (
          styleLayer.type === 'symbol' &&
          styleLayer.layout?.['text-field']
        ) {
          mlMap.setLayoutProperty(styleLayer.id, 'text-field', [
            'coalesce',
            ['get', 'name:ko'],
            ['get', 'name'],
          ]);
        }
      }
    });

    // Detect tile loading errors for OSM fallback
    mlMap.on('error', (e: { error?: { status?: number } }) => {
      if (e.error?.status === 0 || e.error?.status === 404) {
        onErrorRef.current?.();
      }
    });

    return () => {
      if (attribution && map.attributionControl) {
        map.attributionControl.removeAttribution(attribution);
      }
      map.removeLayer(layer);
      layerRef.current = null;
    };
  }, [map, style, attribution]);

  return null;
}
